// Versão mobile da linha da Logística: card vertical em vez de linha horizontal.
// Aparece só em <md. Em ≥md a LinhaLogistica (tabela) é mostrada.
//
// Mostra todas as ações principais: status, prazo, telefone (WhatsApp),
// endereço (Google Maps), edição inline do resumo e botões de ajuste/excluir.

import { Phone, MapPin, AlertTriangle, Wrench, Trash2, Split } from 'lucide-react'
import { formatCurrency, formatTelefone } from '../utils/fmt'
import { TerceirizadosPopover } from './Terceirizados'

const STATUS_STYLE = {
  CADASTRADO: { bg: '#94A3B8', text: '#fff' },
  FABRICANDO: { bg: '#FBBF24', text: '#111' },
  EXPEDICAO:  { bg: '#4ADE80', text: '#111' },
  ROTA:       { bg: '#FB923C', text: '#fff' },
  ENTREGUE:   { bg: '#F9A8D4', text: '#9D174D' },
  AJUSTE:     { bg: '#93C5FD', text: '#1E40AF' },
  POS_VENDA:  { bg: '#F9A8D4', text: '#9D174D' },
}

const HORARIOS = ['07h - 09h', '09h - 11h', '11h - 13h', '13h - 16h', '16h - 19h', '19h - 22h']

const STATUS_LABEL = {
  CADASTRADO: 'Cadastrado',
  FABRICANDO: 'Fabricando',
  EXPEDICAO:  'Expedição',
  ROTA:       'Rota',
  ENTREGUE:   'Entregue',
  AJUSTE:     'Ajuste',
  POS_VENDA:  'Pós-venda',
}

export function LinhaLogisticaMobile({ pedido, onChange, onAbrirAjuste, onDelete, statusOptions }) {
  const s = STATUS_STYLE[pedido.status] || STATUS_STYLE.CADASTRADO
  const tel = pedido.cliente?.contato || ''
  const enderecoMaps = [pedido.cliente?.endereco, pedido.cliente?.bairro].filter(Boolean).join(', ')
  const ehAjuste = !!pedido.tipoAjuste
  const ehFilho  = !!pedido.tipoFilho
  const podeAbrirAjuste = (pedido.status === 'ENTREGUE' || pedido.status === 'POS_VENDA') && !ehAjuste

  function update(campo, valor) { onChange({ ...pedido, [campo]: valor }) }
  function updateCliente(campo, valor) {
    const v = campo === 'contato' ? formatTelefone(valor) : valor
    onChange({ ...pedido, cliente: { ...(pedido.cliente || {}), [campo]: v } })
  }

  const atrasada      = pedido.prazoOriginal && pedido.prazoOriginal !== pedido.prazo
  const atencaoManual = !!pedido.atencao && !atrasada

  return (
    <div
      className={`rounded-xl border-2 mb-2 overflow-hidden shadow-sm
        ${atrasada
          ? 'bg-red-50 border-red-200'
          : atencaoManual
            ? 'bg-orange-50 border-orange-200'
            : ehAjuste
              ? 'bg-amber-50/50 border-amber-200'
              : ehFilho
                ? 'bg-indigo-50/50 border-indigo-200'
                : 'bg-white border-slate-200'}`}
    >
      {/* Header: status + nome cliente + n° */}
      <div className="px-3 py-2.5 border-b border-slate-100 flex items-start gap-2">
        <select
          value={pedido.status}
          onChange={e => update('status', e.target.value)}
          style={{ backgroundColor: s.bg, color: s.text }}
          className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide border-0 outline-none appearance-none shrink-0"
        >
          {(statusOptions || Object.entries(STATUS_LABEL)).map(([id, label]) =>
            Array.isArray(id) ? null : <option key={id} value={id}>{label}</option>
          )}
          {/* Alternativa: se statusOptions vier como array de objetos */}
        </select>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-800 leading-tight truncate flex items-center gap-1">
            {ehAjuste && <Wrench size={11} className="text-amber-600 shrink-0" />}
            {ehFilho && <Split size={11} className="text-indigo-600 shrink-0" />}
            {pedido.cliente?.nome || '—'}
          </p>
          <p className="text-[10px] font-mono text-slate-400 mt-0.5">{pedido.numero}</p>
        </div>

        {/* Atenção toggle */}
        <button
          type="button"
          onClick={() => update('atencao', !pedido.atencao)}
          className={`p-1.5 rounded-lg shrink-0 transition-colors
            ${pedido.atencao
              ? 'text-orange-600 bg-orange-100'
              : 'text-slate-300 active:text-orange-500'}`}
        >
          <AlertTriangle size={14} fill={pedido.atencao ? 'currentColor' : 'none'} />
        </button>
      </div>

      {/* Resumo + Bairro */}
      <div className="px-3 py-2 space-y-1.5">
        {pedido.bairro && (
          <p className="text-[11px] text-slate-500">
            <span className="font-semibold text-slate-700">{pedido.bairro}</span>
          </p>
        )}
        {pedido.resumo && (
          <p className="text-xs text-slate-700 leading-snug whitespace-pre-wrap">{pedido.resumo}</p>
        )}
      </div>

      {/* Horário + Data */}
      <div className="px-3 py-2 bg-slate-50/60 border-y border-slate-100 grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <p className="text-[9px] uppercase text-slate-400 font-bold mb-0.5">Data</p>
          <input
            type="date"
            value={pedido.prazo || ''}
            onChange={e => update('prazo', e.target.value)}
            className="w-full text-xs font-semibold text-slate-700 bg-transparent border-0 outline-none"
          />
          {pedido.prazoOriginal && pedido.prazoOriginal !== pedido.prazo && (
            <p className="text-[9px] text-red-600 mt-0.5">antes: {pedido.prazoOriginal}</p>
          )}
        </div>
        <div>
          <p className="text-[9px] uppercase text-slate-400 font-bold mb-0.5">Horário</p>
          <select
            value={pedido.horarioEntrega || ''}
            onChange={e => update('horarioEntrega', e.target.value)}
            className="w-full text-xs font-semibold text-slate-700 bg-transparent border-0 outline-none"
          >
            <option value="">—</option>
            {HORARIOS.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
      </div>

      {/* Endereço */}
      {pedido.cliente?.endereco && (
        <div className="px-3 py-2 border-b border-slate-100">
          <p className="text-[9px] uppercase text-slate-400 font-bold mb-0.5">Endereço</p>
          <p className="text-[11px] text-slate-600 leading-snug">{pedido.cliente.endereco}</p>
        </div>
      )}

      {/* Footer: ações em pílulas grandes touch-friendly */}
      <div className="px-2 py-2 flex items-center gap-1.5 bg-slate-50/40">
        {tel && (
          <a
            href={`https://wa.me/55${tel.replace(/\D/g, '').replace(/^55/, '')}`}
            target="_blank" rel="noreferrer"
            onClick={e => e.stopPropagation()}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-[11px] font-bold active:bg-emerald-100"
          >
            <Phone size={13} />
            <span className="truncate">{formatTelefone(tel)}</span>
          </a>
        )}
        {enderecoMaps && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(enderecoMaps)}`}
            target="_blank" rel="noreferrer"
            onClick={e => e.stopPropagation()}
            className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-[11px] font-bold active:bg-blue-100 flex items-center gap-1"
          >
            <MapPin size={13} />
            Maps
          </a>
        )}
        <TerceirizadosPopover pedido={pedido} onChange={onChange} />
        {podeAbrirAjuste && (
          <button
            onClick={e => { e.stopPropagation(); onAbrirAjuste?.(pedido) }}
            className="p-2 rounded-lg text-amber-600 bg-amber-50 active:bg-amber-100"
            title="Abrir ajuste"
          >
            <Wrench size={14} />
          </button>
        )}
        <button
          onClick={() => onDelete(pedido.id)}
          className="p-2 rounded-lg text-slate-300 active:text-red-500 active:bg-red-50"
          title="Excluir pedido"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Pendente — se houver */}
      {pedido.valorPendenteEntrega > 0 && (
        <div className="px-3 py-2 bg-amber-50/40 border-t border-amber-100 flex items-center justify-between text-xs">
          <span className="text-[10px] uppercase text-amber-700 font-bold">A receber</span>
          <span className="text-sm font-bold text-[#CC8800]">{formatCurrency(pedido.valorPendenteEntrega)}</span>
        </div>
      )}
    </div>
  )
}
